import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaWhatsapp, FaFacebookF, FaInstagram } from 'react-icons/fa';
import { BsTwitterX } from 'react-icons/bs';
import Input from '../components/ui/Input';
import Select, { SelectItem } from '../components/ui/Select';
import Button from '../components/ui/Button';
import Skeleton from '../components/ui/Skeleton';
import Listing from '../components/block/listing';

const CONTACT_PAGE_ID = '66c6f093dc2c7f86bdc18469';
const ABOUT_PAGE_ID = '66c6eeef6fd2b5788b909128';

function BlogPost() {
  const { post } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    category: '',
    fullName: '',
    phoneNumber: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [post]);

  const fetchPost = async () => {
    try {
      const response = await fetch('/api/posts');
      let data = await response.json();
      data = data.filter(item => item._id === post);
      if (data.length > 0) {
        setBlogPost(data[0]);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      setSubmitSuccess(true);
      setFormData({
        category: '',
        fullName: '',
        phoneNumber: '',
        email: '',
        message: ''
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      alert('Failed to submit contact form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Skeleton className="w-16 h-16 rounded-full" />
      </div>
    );
  }

  // Render Contact Us page
  if (post === CONTACT_PAGE_ID) {
    return (
      <div className="min-h-screen bg-white py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We are always available to assist you. Our Omni-Channel contact centre agents are available 24/7 to support you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Left Panel - Contact Information */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div
                className="relative h-full min-h-[500px] bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200&q=80)',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-gray-800/70 to-gray-900/80"></div>
                
                <div className="relative h-full flex flex-col justify-center p-8 md:p-12 text-white">
                  <div className="space-y-8">
                    <div>
                      <p className="text-lg md:text-xl mb-4 font-medium">
                        Reach us on Stella our virtual assistant on
                      </p>
                      <a
                        href="https://wa.me/2348139851110"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        <FaWhatsapp className="text-2xl" />
                        <span className="font-semibold">WhatsApp</span>
                      </a>
                    </div>

                    <div>
                      <p className="text-lg md:text-xl mb-4 font-medium">
                        or on our social channels
                      </p>
                      <div className="flex items-center gap-4">
                        <a
                          href="#"
                          className="w-12 h-12 bg-gray-800/50 hover:bg-black rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                        >
                          <BsTwitterX className="w-6 h-6" />
                        </a>
                        <a
                          href="#"
                          className="w-12 h-12 bg-gray-800/50 hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                        >
                          <FaFacebookF className="w-6 h-6" />
                        </a>
                        <a
                          href="#"
                          className="w-12 h-12 bg-gray-800/50 hover:bg-gradient-to-r hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#833AB4] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                        >
                          <FaInstagram className="w-6 h-6" />
                        </a>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-white/20">
                      <p className="text-lg md:text-xl mb-4 font-medium">
                        You can also call us on
                      </p>
                      <a
                        href="tel:+2348139851110"
                        className="flex items-center gap-3 text-xl md:text-2xl font-semibold hover:text-green-400 transition-colors mb-6"
                      >
                        <FaPhone className="text-[#DC2626]" />
                        <span>08139851110</span>
                      </a>
                      
                      <p className="text-lg md:text-xl mb-2 font-medium">
                        or send us an email on
                      </p>
                      <a
                        href="mailto:contact@gigaautos.com"
                        className="flex items-center gap-3 text-xl md:text-2xl font-semibold text-[#DC2626] hover:text-red-400 transition-colors"
                      >
                        <FaEnvelope className="text-[#DC2626]" />
                        <span>contact@gigaautos.com</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Customer Feedback Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Customer Feedback
              </h2>

              {submitSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="text-4xl mb-3">✅</div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    Thank You!
                  </h3>
                  <p className="text-green-700">
                    Your message has been sent successfully. We&apos;ll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Category</label>
                    <Select
                      placeholder="Select Category"
                      selectedKeys={formData.category ? new Set([formData.category]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] || '';
                        handleInputChange('category', selected);
                      }}
                      className="w-full"
                    >
                      <SelectItem key="general" value="General Inquiry">General Inquiry</SelectItem>
                      <SelectItem key="support" value="Customer Support">Customer Support</SelectItem>
                      <SelectItem key="sales" value="Sales">Sales</SelectItem>
                      <SelectItem key="feedback" value="Feedback">Feedback</SelectItem>
                      <SelectItem key="complaint" value="Complaint">Complaint</SelectItem>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name</label>
                    <Input
                      placeholder="Full Name"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      className="w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
                    <Input
                      placeholder="Phone Number"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      className="w-full"
                      type="tel"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                    <Input
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full"
                      type="email"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                    <textarea
                      placeholder="Enter your message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-200 bg-white min-h-[120px]"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-6 text-base transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render About Us page
  if (post === ABOUT_PAGE_ID) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-20 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-200/20 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20 md:mb-24">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
              <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
              <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
                About GIGA Mobility
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-8 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                The modern way to
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                own your dream car
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium mb-8">
              GIGA Mobility is an African technology-powered company, providing seamless automotive services to customers across Africa.
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-20">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-1.5 w-16 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Who We Are
                </h2>
              </div>
              <div className="space-y-6">
                <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium">
                  At <span className="text-[#DC2626] font-bold">GIGA Mobility</span>, we are your trusted partner for all automotive needs. We offer comprehensive services to make your automotive journey seamless and convenient.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#DC2626] rounded-full mt-2.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-lg md:text-xl text-gray-900 font-bold mb-1">Car Dealer</p>
                      <p className="text-gray-700 leading-relaxed">Browse and purchase quality vehicles from our extensive inventory of new and pre-owned cars.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#DC2626] rounded-full mt-2.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-lg md:text-xl text-gray-900 font-bold mb-1">Car Rental</p>
                      <p className="text-gray-700 leading-relaxed">Rent premium vehicles by the hour, day, or week for your travel and business needs.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#DC2626] rounded-full mt-2.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-lg md:text-xl text-gray-900 font-bold mb-1">Auto Workshop</p>
                      <p className="text-gray-700 leading-relaxed">Professional repair and maintenance services to keep your vehicle running at its best.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium">
                    <span className="text-[#DC2626] font-bold">GIGA Mobility</span> (God is God Autos) is owned by <span className="font-bold">Alphanumber Inc, USA</span>, a leading logistics and transportation company specializing in logistics services, car export, and shipping facilitation. Operating as a registered entity in Nigeria, we bring international expertise and local knowledge to serve our customers across Africa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular blog post rendering
  if (!blogPost) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Post Not Found</h2>
          <p className="text-gray-600">The blog post you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/blog" className="text-[#DC2626] hover:underline mt-4 inline-block">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 py-12 md:py-20 px-4 md:px-8">
      <div className="w-full m-auto md:w-3/4">
        <div className="my-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{blogPost.title}</h1>
          <p className="text-gray-600">
            Date: {new Date(blogPost.date).toLocaleDateString()} | Category: {blogPost.category || 'General'}
          </p>
        </div>
        {blogPost.image && (
          <img className="w-full image-post rounded-md mb-6" src={blogPost.image} alt={blogPost.title} />
        )}
        <div
          className="ck-content mt-6 px-4 prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: blogPost.content || '' }}
        ></div>
      </div>

      <div className="px-1 md:px-20 mt-12">
        <Listing />
      </div>
    </div>
  );
}

export default BlogPost;
